// hooks/useConfig.js - React Hook for Configuration
import { useState, useEffect } from 'react';
import { getConfig, getEmail, getCompanyInfo } from '../utils/config';

/**
 * Custom hook to access application configuration
 * @returns {Object} Configuration object with emails, company info, and loading state
 */
export function useConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const data = await getConfig();
        setConfig(data);
      } catch (err) {
        setError(err);
        console.error('Failed to load config:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchConfig();
  }, []);

  return { config, loading, error };
}

/**
 * Custom hook to get a specific email address
 * @param {string} type - Email type (support, sales, legal, privacy, noreply)
 * @returns {Object} Email address and loading state
 */
export function useEmail(type) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEmail() {
      try {
        const emailAddress = await getEmail(type);
        setEmail(emailAddress);
      } catch (err) {
        console.error(`Failed to load ${type} email:`, err);
        setEmail(`${type}@snaptura.online`); // Fallback
      } finally {
        setLoading(false);
      }
    }

    fetchEmail();
  }, [type]);

  return { email, loading };
}

/**
 * Custom hook to get company information
 * @returns {Object} Company name, website, and loading state
 */
export function useCompanyInfo() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCompanyInfo() {
      try {
        const companyData = await getCompanyInfo();
        setCompany(companyData);
      } catch (err) {
        console.error('Failed to load company info:', err);
        setCompany({ name: 'Snaptura', website: 'https://snaptura.online' }); // Fallback
      } finally {
        setLoading(false);
      }
    }

    fetchCompanyInfo();
  }, []);

  return { company, loading };
}

export default useConfig;
